import { useEffect, useState } from "react";
import ChatMessages from "./ChatMessagesStreaming";
import ModeSelector from "./ModeSelector";

// Assets
import axiSymbol from "../assets/axiSymbol.svg";
import telephoneDial from "../static/telephone_dial_sound.wav";

// Constants
import { APP_CONFIG, VOICE_CONFIG, FIXED_LANGUAGES } from "../constants";

const { BACKEND_URL: backendUrl, APP_ENV: appEnv } = APP_CONFIG;
const { DEFAULT_REGION } = VOICE_CONFIG;

let regionCode = DEFAULT_REGION;
if (backendUrl.startsWith("https")) {
  regionCode = backendUrl.split(".")[1];
}

const renderOption = (lang, index) => (
  <option key={index} value={lang.value} disabled={lang.disabled}>
    {lang.label}
  </option>
);

const getLanguageOptions = (venueLangCodeList) => {
  if (
    appEnv !== "marketready" &&
    venueLangCodeList &&
    venueLangCodeList.length > 0
  ) {
    return venueLangCodeList.map(renderOption);
  } else {
    const regionLanguages = FIXED_LANGUAGES.filter(
      (lang) => lang.region === regionCode || lang.region === "all"
    );
    return regionLanguages.map(renderOption);
  }
};

const getDefaultVoice = (venueLangCodeList) => {
  if (
    appEnv !== "marketready" &&
    venueLangCodeList &&
    venueLangCodeList.length > 0
  ) {
    return {
      voiceValue: venueLangCodeList[0].value,
      languageCode: venueLangCodeList[0].value,
    };
  }
  const regionLanguages = FIXED_LANGUAGES.filter(
    (lang) => lang.region === regionCode || lang.region === "all"
  );
  if (regionLanguages.length > 0) {
    return { voiceValue: regionLanguages[0].value, languageCode: "en-US" };
  }
  return { voiceValue: "", languageCode: "en-US" };
};

function Chat({
  appId,
  systemStatus,
  setSystemStatus,
  messages,
  setMessages,
  setLastMessage,
  servicePlace,
  merchantURL,
  integrationType,
  audioStream,
  requestMicrophoneAccess,
  displaySummaryWindow,
  setDisplaySummaryWindow,
  langName,
  venueLangCodeList,
  presetMode,
  chatRef,
}) {
  // Mode selection (null until user picks Voice or Text)
  const [mode, setMode] = useState(null);

  // Language Selection UI
  const [language, setLanguage] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [started, setStarted] = useState(false);
  const [isRequestingMic, setIsRequestingMic] = useState(false);

  const handleSelectMode = (selectedMode) => {
    setMode(selectedMode);
    if (selectedMode === "text") {
      // Text mode skips language selection and mic permission.
      // Use the venue's default voice so /conversations/start still resolves
      // voice/language settings for the assistant response.
      const { voiceValue, languageCode } = getDefaultVoice(venueLangCodeList);
      setLanguage(languageCode);
      setVoiceName(voiceValue);
      setStarted(true);
      setSystemStatus("idle");
    }
  };

  // Embed mode can pre-select Voice/Text via ?mode=, skipping the mode selector.
  // We wait for venueLangCodeList so getDefaultVoice has the data it needs.
  useEffect(() => {
    if (!presetMode || mode !== null) return;
    if (presetMode === "text" && !venueLangCodeList) return;
    handleSelectMode(presetMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetMode, venueLangCodeList]);

  const handleLanguageSelect = (e) => {
    if (
      appEnv !== "marketready" &&
      venueLangCodeList &&
      venueLangCodeList.length > 0
    ) {
      // Use the selected language from venue list
      const selectedLang = venueLangCodeList.find(
        (lang) => lang.value === e.target.value
      );
      setLanguage(selectedLang ? selectedLang.value : e.target.value);
    } else {
      // For fixed list, use language code mapping or default
      setLanguage("en-US");
    }
    setVoiceName(e.target.value);
  };

  const handleBookingStart = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!language) {
      alert("Please select a language");
      return false;
    }

    if (typeof requestMicrophoneAccess === "function") {
      setIsRequestingMic(true);
      try {
        await requestMicrophoneAccess();
      } catch (err) {
        console.error("Microphone permission error:", err);
        alert(
          "Microphone access is required to speak with Axi. Please allow microphone access and try again."
        );
        return;
      } finally {
        setIsRequestingMic(false);
      }
    }
    const audio = new Audio(telephoneDial);
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((playError) => {
        console.warn("Unable to play start tone:", playError);
      });
    }
    setSystemStatus("connecting");
    setStarted(true);

    setTimeout(() => {
      setSystemStatus("idle");
    }, 2000);
  };

  const buttonLabel = isRequestingMic
    ? "Allow microphone..."
    : systemStatus === "connecting"
    ? "Connecting..."
    : "Start Booking";

  // Step 1: mode selection screen
  if (mode === null) {
    return <ModeSelector appId={appId} onSelectMode={handleSelectMode} />;
  }

  // Step 3: chat in progress (voice or text)
  if (
    systemStatus === "idle" ||
    systemStatus === "playing" ||
    systemStatus === "complete" ||
    systemStatus === "waiting"
  ) {
    return (
      <ChatMessages
        ref={chatRef}
        appId={appId}
        started={started}
        mode={mode}
        voiceName={voiceName}
        servicePlace={servicePlace}
        merchantURL={merchantURL}
        integrationType={integrationType}
        audioStream={audioStream}
        messages={messages}
        language={language}
        langName={langName}
        setMessages={setMessages}
        setSystemStatus={setSystemStatus}
        setLastMessage={setLastMessage}
        displaySummaryWindow={displaySummaryWindow}
        setDisplaySummaryWindow={setDisplaySummaryWindow}
        requestMicrophoneAccess={requestMicrophoneAccess}
      />
    );
  }

  // Step 2 (voice mode only): language selection + Start Booking
  return (
    <main
      id={"axify__widget-" + appId + "-content"}
      className="axify__widget-content"
    >
      <section
        id={"axify__widget-" + appId + "-intro"}
        className="axify__widget-intro"
      >
        <div className="axify__widget-intro-inner">
          <img src={axiSymbol} width="72" height="72" alt="Axi Symbol" />
          <p>Speak to Axi to assist you with your booking today.</p>
          <div className="axify__widget-ui-select-wrapper">
            <select
              name="langs"
              id={"axify__widget-" + appId + "-langs"}
              className="axify__widget-ui-select"
              onChange={handleLanguageSelect}
            >
              <option value="">Select language</option>
              {getLanguageOptions(venueLangCodeList)}
            </select>
          </div>
          <button
            id={"axify__widget-" + appId + "-startBooking"}
            className="axify__widget-ui-button start"
            type="button"
            onClick={handleBookingStart}
            disabled={isRequestingMic || systemStatus === "connecting"}
          >
            <span className="label" aria-label="Start Booking Venue">
              {buttonLabel}
            </span>
          </button>
        </div>
      </section>
    </main>
  );
}

export default Chat;
